import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  type ReactNode,
} from "react"
import type { User, Session } from "@supabase/supabase-js"
import { supabase } from "./supabase"

const ALLOWED_DOMAIN = "offseasonwellness.com"

interface AuthContextValue {
  user: User | null
  session: Session | null
  loading: boolean
  error: string | null
  signInWithGoogle: () => Promise<void>
  signInWithPassword: (email: string, password: string) => Promise<void>
  signOut: () => Promise<void>
  clearError: () => void
}

const AuthContext = createContext<AuthContextValue | null>(null)

function getEmailDomain(email: string): string {
  return email.split("@")[1]?.toLowerCase() ?? ""
}

function isAllowedDomain(email: string): boolean {
  return getEmailDomain(email) === ALLOWED_DOMAIN
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!supabase) {
      setLoading(false)
      return
    }

    const client = supabase
    let mounted = true

    // Get initial session (also processes OAuth hash redirect)
    client.auth.getSession().then(({ data: { session: s } }) => {
      if (!mounted) return

      if (s?.user) {
        const email = s.user.email ?? ""
        if (isAllowedDomain(email)) {
          setSession(s)
          setUser(s.user)
        } else {
          client.auth.signOut()
          setError(`Only @${ALLOWED_DOMAIN} emails are allowed`)
        }
      }
      setLoading(false)
    }).catch((err) => {
      if (!mounted) return
      console.error("Auth getSession failed:", err)
      setLoading(false)
    })

    // Clean up OAuth hash fragment from URL after Supabase processes it
    if (window.location.hash.includes("access_token") || window.location.hash.includes("error")) {
      // Use setTimeout to let Supabase process the hash first
      setTimeout(() => {
        window.history.replaceState(null, "", window.location.pathname + window.location.search)
      }, 100)
    }

    // Listen for subsequent auth changes (token refresh, sign out, etc.)
    const {
      data: { subscription },
    } = client.auth.onAuthStateChange((event, s) => {
      if (!mounted) return

      // Skip INITIAL_SESSION — already handled above
      if (event === "INITIAL_SESSION") return

      if (s?.user) {
        const email = s.user.email ?? ""
        if (isAllowedDomain(email)) {
          setSession(s)
          setUser(s.user)
          setError(null)
        } else {
          client.auth.signOut()
          setSession(null)
          setUser(null)
          setError(`Only @${ALLOWED_DOMAIN} emails are allowed`)
        }
      } else {
        setSession(null)
        setUser(null)
      }
      setLoading(false)
    })

    return () => {
      mounted = false
      subscription.unsubscribe()
    }
  }, [])

  const signInWithGoogle = useCallback(async () => {
    if (!supabase) return
    setError(null)
    setLoading(true)

    const redirectTo =
      (import.meta.env.VITE_PUBLIC_SITE_URL as string | undefined)?.replace(/\/$/, "") ||
      window.location.origin

    const { error: oauthError } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo,
      },
    })

    if (oauthError) {
      setError(String(oauthError.message))
      setLoading(false)
    }
    // After this, browser redirects to Google, then back.
    // onAuthStateChange + getSession handle the callback.
  }, [])

  const signInWithPassword = useCallback(async (email: string, password: string) => {
    if (!supabase) return
    setError(null)

    if (!isAllowedDomain(email)) {
      setError(`Only @${ALLOWED_DOMAIN} emails are allowed`)
      return
    }

    setLoading(true)
    const { error: signInError } = await supabase.auth.signInWithPassword({
      email,
      password,
    })

    if (signInError) {
      setError("Invalid email or password")
    }
    setLoading(false)
  }, [])

  const signOut = useCallback(async () => {
    if (!supabase) return
    await supabase.auth.signOut()
    setUser(null)
    setSession(null)
    setError(null)
  }, [])

  const clearError = useCallback(() => {
    setError(null)
  }, [])

  return (
    <AuthContext.Provider
      value={{
        user,
        session,
        loading,
        error,
        signInWithGoogle,
        signInWithPassword,
        signOut,
        clearError,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) {
    throw new Error("useAuth must be used within an AuthProvider")
  }
  return ctx
}
